import { useState } from "react";
import {
  Button,
  PocketRow,
  PocketSection,
  PocketStatement,
} from "@/components/ui";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsConnectionsMessages } from "@/i18n/messages/settings-connections";
import { isTauri } from "@/lib/is-tauri";
import { useCompanionStore } from "@/stores/companion-store";
import {
  isPersonalRuntime,
  isRuntimeType,
  personalRuntime,
} from "@/desktop/onboarding/post-demo-runtime-preference";
import { DesktopToolsExplanation } from "./ai-tools-page";
import { CliLoginsSection } from "./cli-logins-section";
import { ConnectionToolAction } from "./connection-tool-action";
import {
  CONNECTION_TOOLS,
  runtimeCommand,
  runtimeStatus,
  runtimeTitle,
  useConnectionRuntimeRefresh,
} from "./connections-state";
import { useSettingsNav } from "./settings-navigation";
import { copyToClipboard } from "@/lib/copy-to-clipboard";

export function ConnectionToolPage({ tool }: { tool: string }) {
  const t = useT(settingsConnectionsMessages);
  const tCommon = useT(commonMessages);
  const nav = useSettingsNav();
  const runtimes = useCompanionStore((state) => state.availableRuntimes);
  const connected = useCompanionStore((state) => state.status === "connected");
  const runtime =
    (isRuntimeType(tool) ? personalRuntime(runtimes, tool) : undefined) ??
    runtimes.find((candidate) => candidate.type === tool);
  const usable = isTauri() || connected;
  const command = runtimeCommand(tool, runtime);
  const { error, refresh } = useConnectionRuntimeRefresh(
    usable && Boolean(command),
  );
  const [copyState, setCopyState] = useState<"copied" | "failed" | null>(null);
  const info = CONNECTION_TOOLS.find((candidate) => candidate.id === tool);
  async function copy(value: string) {
    setCopyState((await copyToClipboard(value)) ? "copied" : "failed");
  }
  if (!info)
    return <PocketStatement size="panel" title={t("toolUnavailable")} />;
  if (!usable) return <DesktopToolsExplanation />;
  const status = runtimeStatus(runtime);
  return (
    <div data-testid="settings-page-connection-tool">
      <PocketStatement
        size="panel"
        title={runtimeTitle(info.name, runtime)}
        line={runtime?.managed ? t("toolBundled") : undefined}
      />
      <ConnectionToolAction runtime={runtime} />
      <PocketSection>
        <PocketRow
          testId="connection-tool-status"
          title={t("status")}
          trailing={
            <span className="block max-w-40 truncate" title={status}>
              {status}
            </span>
          }
        />
        {runtime?.version && (
          <PocketRow
            testId="connection-tool-version"
            title={t("version")}
            trailing={runtime.version}
          />
        )}
        {runtime?.path && (
          <PocketRow
            testId="connection-tool-location"
            title={t("location")}
            secondLine={<span className="select-text">{runtime.path}</span>}
            trailing={
              <Button
                size="sm"
                variant="ghost"
                data-testid="connection-tool-copy-location"
                onClick={() => void copy(runtime.path)}
              >
                {tCommon("copy")}
              </Button>
            }
          />
        )}
        {runtime?.managed && runtime.update_available && (
          <PocketRow
            testId="connection-tool-update-denker"
            title={t("updateDenker")}
            chevron
            onSelect={() => nav.open({ page: "preferences-updates" })}
          />
        )}
      </PocketSection>
      {tool === "claude_cli" &&
        isTauri() &&
        runtime?.path &&
        isPersonalRuntime(runtime) && (
          <CliLoginsSection executablePath={runtime.path} />
        )}
      {command && (
        <PocketSection title={t("terminalCommand")}>
          <div className="px-2">
            <code className="select-text break-all text-appkit-body text-primary">
              {command}
            </code>
            <Button
              size="sm"
              variant="ghost"
              data-testid="connection-tool-copy-command"
              onClick={() => void copy(command)}
            >
              {tCommon("copy")}
            </Button>
          </div>
        </PocketSection>
      )}
      {copyState && (
        <p role="status" className="px-2 text-appkit-small text-muted">
          {copyState === "copied" ? t("copiedSentence") : t("copyFailed")}
        </p>
      )}
      {error && (
        <p role="alert" className="px-2 text-appkit-small text-danger">
          {error}
        </p>
      )}
      <Button
        variant="ghost"
        size="sm"
        data-testid="connection-tool-check"
        onClick={refresh}
      >
        {t("checkAgain")}
      </Button>
    </div>
  );
}
